import { FotaAPIService } from './AxiosAPIService';
import { logger } from './SharedService';
import {
  AudioSchemasResponse,
  AudioSchema,
  AudioSchemaPostRequest,
  AudioSchemaPerFleet,
  AudioSchemaPerDevice,
  AudioSchemaPatchRequest,
  AudioSchemaDeleteRequest,
  AudioSchemaAudioPatchRequest,
  AudioSchemaActivateFleetRequest,
  ApplyAudioSchemaToFleetRequest,
  ApplyAudioSchemaToDeviceRequest,
} from '../types/AudioSchemas';

// GET all audio schemas per fleet
export const getAudioSchemas = async (fleetId: string, query?: string): Promise<AudioSchemasResponse> => {
  logger.info('GET', 'REQUEST', 'getAudioSchemasByFleetId', `/fleets/${fleetId}/audio-bundles${query}`);
  const { data } = await FotaAPIService.api
    .get<AudioSchemasResponse>(`/fleets/${fleetId}/audio-bundles${query}`)
    .catch((e) => Promise.reject(e.response.data));
  logger.info('GET', 'RESPONSE', 'getAudioSchemasByFleetId', data);
  return data;
};

// GET audio schema by id
export const getAudioSchema = async (
  fleetId: string,
  id: string,
): Promise<AudioSchema> => {
  logger.info('GET', 'REQUEST', 'getAudioSchema', `/fleets/${fleetId}/audio-bundles/${id}`);
  const { data } = await FotaAPIService.api
    .get<AudioSchema>(`/fleets/${fleetId}/audio-bundles/${id}`)
    .catch((e) => Promise.reject(e.response.data));
  logger.info('GET', 'RESPONSE', 'getAudioSchema', data);
  return data;
};

// GET active audio schema per fleet
export const getActiveAudioSchemaFleet = async (
  fleetId: string,
): Promise<AudioSchemaPerFleet> => {
  logger.info('GET', 'REQUEST', 'getActiveAudioSchemaPerFleet', `/fleets/${fleetId}/audio-bundles/audio-bundles-fleet`);
  const { data } = await FotaAPIService.api
    .get<AudioSchemaPerFleet>(`/fleets/${fleetId}/audio-bundles/audio-bundles-fleet`)
    .catch((e) => Promise.reject(e.response.data));
  logger.info('GET', 'RESPONSE', 'getActiveAudioSchemaPerFleet', data);
  return data;
};

// GET active audio schema per fleet
export const getActiveAudioSchemaDevice = async (
  fleetId: string,
  deviceId: string,
): Promise<AudioSchemaPerDevice> => {
  logger.info('GET', 'REQUEST', 'getActiveAudioSchemaDevice', `/fleets/${fleetId}/audio-bundles/audio-bundles-device/devices/${deviceId}`);
  const { data } = await FotaAPIService.api
    .get<AudioSchemaPerDevice>(`/fleets/${fleetId}/audio-bundles/audio-bundles-device/devices/${deviceId}`)
    .catch((e) => Promise.reject(e.response.data));
  logger.info('GET', 'RESPONSE', 'getActiveAudioSchemaDevice', data);
  return data;
};

// update name for existing audio schema
export const patchAudioSchema = async ({
  fleetId,
  audioSchemaId,
  name,
}: AudioSchemaPatchRequest): Promise<AudioSchema> => {
  logger.info('PATCH', 'REQUEST', 'patchAudioSchema', `/fleets/${fleetId}/audio-bundles/${audioSchemaId}`);
  const { data } = await FotaAPIService.api
    .patch<AudioSchema>(`/fleets/${fleetId}/audio-bundles/${audioSchemaId}`, {
    name,
  })
    .catch((e) => Promise.reject(e.response.data));
  logger.info('PATCH', 'RESPONSE', 'patchAudioSchema', data);
  return data;
  // return SCHEMAS_MOCK.results[0];
};

// activate usage of audio schema by fleet
export const patchActivateFleetAudioSchema = async ({
  fleetId,
  isActive,
}: AudioSchemaActivateFleetRequest): Promise<AudioSchema> => {
  logger.info('PATCH', 'REQUEST', 'patchActivateFleetAudioSchema', `/fleets/${fleetId}/audio-bundles`);
  const { data } = await FotaAPIService.api
    .patch<AudioSchema>(`/fleets/${fleetId}/audio-bundles`, {
    isActive,
  })
    .catch((e) => Promise.reject(e.response.data));
  logger.info('PATCH', 'RESPONSE', 'patchActivateFleetAudioSchema', data);
  return data;
};

// create new audio schema in fleet
export const postAudioSchema = async ({
  fleetId,
  name,
}: AudioSchemaPostRequest): Promise<AudioSchema> => {
  logger.info('POST', 'REQUEST', 'postAudioSchema', `/fleets/${fleetId}/audio-bundles`);
  const { data } = await FotaAPIService.api
    .post<AudioSchema>(`/fleets/${fleetId}/audio-bundles`, {
    name,
  })
    .catch((e) => Promise.reject(e.response.data));
  logger.info('POST', 'RESPONSE', 'postAudioSchema', data);
  return data;
};

// delete audio schema from fleet
export const deleteAudioSchema = async (payload: AudioSchemaDeleteRequest): Promise<AudioSchema> => {
  const { fleetId, audioSchemaId } = payload;
  logger.info('DELETE', 'REQUEST', 'deleteAudioSchema', `/fleets/${fleetId}/audio-bundles/${audioSchemaId}`);
  const { data } = await FotaAPIService.api.delete<AudioSchema>(`/fleets/${fleetId}/audio-bundles/${audioSchemaId}`);
  logger.info('DELETE', 'RESPONSE', 'deleteAudioSchema', data);
  return data;
};

// patch audio file per scheme
export const patchAudioSchemaFile = async ({
  fleetId,
  audioBundleId,
  audioAlertName,
  description,
  file,
}: AudioSchemaAudioPatchRequest): Promise<AudioSchema> => {
  logger.info('PATCH', 'REQUEST', 'patchAudioSchemaFile', `/fleets/${fleetId}/audio-bundles/${audioBundleId}/audio-schema`);

  const input = new FormData();
  input.append('audioAlertName', audioAlertName);
  input.append('description', description);
  input.append('file', file);

  const { data } = await FotaAPIService.api
    .patch<AudioSchema>(`/fleets/${fleetId}/audio-bundles/${audioBundleId}/audio-schema`, input, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
    .catch((e) => Promise.reject(e.response.data));

  logger.info('PATCH', 'RESPONSE', 'patchAudioSchemaFile', data);
  return data;
  // return SCHEMAS_MOCK.results[0];
};

// apply audio schema to fleet
export const postApplyAudioSchemaToFleet = async ({
  fleetId,
  audioBundleId,
}: ApplyAudioSchemaToFleetRequest): Promise<AudioSchemaPerFleet> => {
  logger.info('POST', 'REQUEST', 'postApplyAudioSchemaToFleet', `/fleets/${fleetId}/audio-bundles/apply`);
  const { data } = await FotaAPIService.api
    .post<AudioSchemaPerFleet>(`/fleets/${fleetId}/audio-bundles/apply`, {
    audioBundleId,
  })
    .catch((e) => Promise.reject(e.response.data));
  logger.info('POST', 'RESPONSE', 'postApplyAudioSchemaToFleet', data);
  return data;
};

// apply audio schema to device
export const postApplyAudioSchemaToDevice = async ({
  fleetId,
  deviceId,
  audioBundleId,
}: ApplyAudioSchemaToDeviceRequest): Promise<AudioSchemaPerDevice> => {
  logger.info('POST', 'REQUEST', 'postApplyAudioSchemaToDevice', `/fleets/${fleetId}/audio-bundles/devices/${deviceId}/apply`);
  const { data } = await FotaAPIService.api
    .post<AudioSchemaPerDevice>(`/fleets/${fleetId}/audio-bundles/devices/${deviceId}/apply`, {
    audioBundleId,
  })
    .catch((e) => Promise.reject(e.response.data));
  logger.info('POST', 'RESPONSE', 'postApplyAudioSchemaToDevice', data);
  return data;
};
